import { api } from 'constants/constant';
import { getHeaders } from 'auth/auth';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { borrowerProfileAPI } from 'redux/services/borrower-profile';
import { adminPipelineAPI } from './pipeline';
import { loanInformationAPI } from './loanInformation';

export const adminDocumentAPI = createApi({
  reducerPath: 'AdminDocument',
  tagTypes: ['updateAdminList', 'documentInsights', 'packageDownloads'],
  baseQuery: fetchBaseQuery({ baseUrl: api, credentials: 'include' }),
  endpoints: (builder) => ({
    getDocumentList: builder.query({
      query: ({ applicationId, userId }) => {
        return {
          url: `b2b/documents/aggregate/loan`,
          method: 'GET',
          params: {
            ...(applicationId ? { applicationId } : {}),
            ...(userId ? { borrowerId: userId } : {}),
          },
          headers: getHeaders(),
        };
      },
      transformResponse: (response) => ({
       
        data: response?.data ?? [],
        total: Array.isArray(response?.data) ? response.data.length : 0,
        skip: 0,
        limit: Array.isArray(response?.data) ? response.data.length : 0,
      }),
      providesTags: ['updateAdminList'],
    }),
    getUserProperty: builder.query({
      query: () => {
        return {
          url: `b2b/documents/user_info`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      transformResponse: (response) => ({
        data: response?.data ?? [],
      }),
    }),
    deleteDocument: builder.mutation({
      query: ({ documentId }) => {
        const headers = getHeaders();
        delete headers.accept;
        headers.Accept = 'application/json';
        return {
          url: `b2b/documents/${documentId}`,
          method: 'DELETE',
          headers,
        };
      },
      invalidatesTags: ['updateAdminList'],
    }),
    uploadPredefinedDocs: builder.mutation({
      query: ({ data, additionalParams }) => {
        const formData = new FormData();
        formData.append('file', data);
        const params = additionalParams || {};
        const normalizedParams = { ...params };

        if (
          normalizedParams.loan_application_id === undefined &&
          params.applicationId !== undefined
        ) {
          normalizedParams.loan_application_id = params.applicationId;
        }
        if (normalizedParams.borrower_id === undefined) {
          if (params.borrowerId !== undefined) {
            normalizedParams.borrower_id = params.borrowerId;
          } else if (params.userId !== undefined) {
            normalizedParams.borrower_id = params.userId;
          }
        }
        if (
          normalizedParams.document_category_id === undefined &&
          params.documentCategoryId !== undefined
        ) {
          normalizedParams.document_category_id = params.documentCategoryId;
        }
        if (normalizedParams.document_type_id === undefined) {
          if (params.documentTypeId !== undefined) {
            normalizedParams.document_type_id = params.documentTypeId;
          } else if (params.documentSubTypeId !== undefined) {
            normalizedParams.document_type_id = params.documentSubTypeId;
          }
        }
        if (normalizedParams.title === undefined && params.documentTitle) {
          normalizedParams.title = params.documentTitle;
        }
        if (
          normalizedParams.document_type_code === undefined &&
          params.documentTypeCode !== undefined
        ) {
          normalizedParams.document_type_code = params.documentTypeCode;
        }

        const appendedKeys = new Set();
        const appendIfDefined = (key, value) => {
          if (value !== undefined && value !== null && value !== '') {
            formData.append(key, value);
            appendedKeys.add(key);
          }
        };

        appendIfDefined('loan_application_id', normalizedParams.loan_application_id);
        appendIfDefined('borrower_id', normalizedParams.borrower_id);
        appendIfDefined('document_category_id', normalizedParams.document_category_id);
        appendIfDefined('document_type_id', normalizedParams.document_type_id);
        appendIfDefined('title', normalizedParams.title);

        Object.entries(normalizedParams).forEach(([key, value]) => {
          if (!appendedKeys.has(key)) {
            appendIfDefined(key, value);
          }
        });

        const headers = getHeaders();
        delete headers['Content-Type'];
        delete headers['content-type'];
        return {
          url: `b2b/documents/upload`,
          method: 'POST',
          headers,
          body: formData,
        };
      },
      invalidatesTags: ['updateAdminList'],
    }),
    updatePredefinedDocs: builder.mutation({
      query: ({ data, additionalParams, documentId }) => {
        const formData = new FormData();
        formData.append('file', data);
        const { versionNotes } = additionalParams || {};
        if (versionNotes) {
          formData.append('version_notes', versionNotes);
        }
        const headers = getHeaders();
        delete headers['Content-Type'];
        delete headers['content-type'];
        return {
          url: `b2b/documents/${documentId}/versions`,
          method: 'POST',
          headers,
          body: formData,
        };
      },
      invalidatesTags: ['updateAdminList'],
    }),
    verifyDocument: builder.mutation({
      query: ({ documentId, comment }) => {
        const headers = getHeaders();
        headers.Accept = 'application/json';
        delete headers['Content-Type'];
        delete headers['content-type'];
        return {
          url: `b2b/documents/${documentId}/approve`,
          method: 'POST',
          headers,
          body: comment ? { notes: comment } : undefined,
        };
      },
      invalidatesTags: ['updateAdminList'],
    }),
    rejectDocument: builder.mutation({
      query: ({ documentId, comment }) => {
        const headers = getHeaders();
        headers.Accept = 'application/json';
        return {
          url: `b2b/documents/${documentId}/reject`,
          method: 'POST',
          headers,
          body: comment ? { notes: comment } : undefined,
        };
      },
      invalidatesTags: ['updateAdminList'],
    }),
    uploadAndClassify: builder.mutation({
      query: ({ data, additionalParams }) => {
        const formData = new FormData();
        formData.append('file', data);
        if (additionalParams?.loan_application_id)
          formData.append('loan_application_id', additionalParams.loan_application_id);
        if (additionalParams?.borrower_id)
          formData.append('borrower_id', additionalParams.borrower_id);
        if (additionalParams?.title)
          formData.append('title', additionalParams.title);
        const headers = getHeaders();
        delete headers['Content-Type'];
        delete headers['content-type'];
        return {
          url: 'b2b/documents/upload-and-classify',
          method: 'POST',
          headers,
          body: formData,
        };
      },
      invalidatesTags: ['updateAdminList'],
    }),
    getDocumentById: builder.query({
      query: ({ documentId }) => ({
        url: `b2b/documents/get`,
        method: 'GET',
        params: { document_id: documentId },
        headers: getHeaders(),
      }),
    }),
    getDocumentSummaries: builder.query({
      query: ({ documentIds }) => ({
        url: 'b2b/documents/summaries',
        method: 'GET',
        params: { document_ids: documentIds.join(',') },
        headers: getHeaders(),
      }),
      // The AI summary lands in Mongo at the END of processing, while the id
      // set stays the same — without a tag the cached "not yet complete"
      // result would never be replaced.
      providesTags: ['documentInsights'],
      transformResponse: (response) => response?.data ?? {},
    }),
    getDocumentAccuracyMetrics: builder.query({
      query: ({ documentIds }) => ({
        url: 'b2b/documents/accuracy-metrics',
        method: 'GET',
        params: { document_ids: documentIds.join(',') },
        headers: getHeaders(),
      }),
      // The AI summary lands in Mongo at the END of processing, while the id
      // set stays the same — without a tag the cached "not yet complete"
      // result would never be replaced.
      providesTags: ['documentInsights'],
      transformResponse: (response) => response?.data ?? {},
    }),
    // Whether this loan may have a pre-approval letter, and why not. The
    // generate endpoint applies the same rules and refuses for itself; this
    // is so the chooser can grey the option out and say why, rather than
    // offering it and then rejecting the click.
    //
    // Lives on this slice, not one of its own, because the letter is filed
    // into the Document Manager: the generate mutation that lands with
    // LH-334 invalidates 'updateAdminList' and the list refreshes itself.
    getPreApprovalEligibility: builder.query({
      query: ({ applicationId }) => ({
        url: `b2b/loan/applications/${applicationId}/pre-approval-letter/eligibility`,
        method: 'GET',
        headers: getHeaders(),
      }),
    }),
    // What became of the letter this loan has already issued (LH-344).
    //
    // Its own call rather than part of the eligibility answer. That one
    // decides whether the Generate row can be clicked and the chooser cannot
    // draw without it; this only annotates the row. The two are fetched
    // together, so the row appears at eligibility speed and the note catches
    // up — rather than the row waiting on a fact it does not depend on.
    //
    // Tagged because half of it is about a filed document: without this,
    // reissuing would leave the chooser still saying a reissue is needed,
    // against the very version that has just dealt with it.
    getPreApprovalLetterStatus: builder.query({
      query: ({ applicationId }) => ({
        url: `b2b/loan/applications/${applicationId}/pre-approval-letter/status`,
        method: 'GET',
        headers: getHeaders(),
      }),
      providesTags: ['updateAdminList'],
    }),
    // What the preview screen opens with: the attestations as the loan's
    // records support them, for the officer to confirm or change.
    getPreApprovalDraft: builder.query({
      query: ({ applicationId }) => ({
        url: `b2b/loan/applications/${applicationId}/pre-approval-letter/draft`,
        method: 'GET',
        headers: getHeaders(),
      }),
    }),
    // The letter as it would be issued, rendered from what the officer has
    // confirmed so far. Returns the PDF itself rather than a description of
    // it, so the preview cannot drift from the document.
    previewPreApprovalLetter: builder.mutation({
      query: ({ applicationId, attestations }) => ({
        url: `b2b/loan/applications/${applicationId}/pre-approval-letter/preview`,
        method: 'POST',
        headers: getHeaders(),
        body: { attestations },
        // A refusal comes back as JSON; only a rendered letter is a blob.
        responseHandler: (response) =>
          response.ok ? response.blob() : response.json(),
      }),
    }),
    // Issue the letter. The server checks eligibility again before doing
    // anything, so a stale or skipped eligibility read cannot get a letter
    // onto a loan that may not have one.
    //
    // Invalidates the document list because the letter is filed into it, and
    // with it the status read, whose account of the issued letter this call
    // has just replaced.
    generatePreApprovalLetter: builder.mutation({
      query: ({ applicationId, attestations }) => ({
        url: `b2b/loan/applications/${applicationId}/pre-approval-letter/generate`,
        method: 'POST',
        headers: getHeaders(),
        body: { attestations },
      }),
      invalidatesTags: ['updateAdminList'],
      // Issuing the letter moves a Pre-approval in progress file to
      // Pre-approved: the loan header and the lists read the new status.
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (!data?.status_moved_to) return;
          dispatch(borrowerProfileAPI.util.invalidateTags(['BorrowerProfile', 'ProfileCompletion']));
          dispatch(adminPipelineAPI.util.invalidateTags(['updateAdminPipeline']));
          dispatch(loanInformationAPI.util.invalidateTags(['dashboardData']));
        } catch {
          // Not issued; nothing moved.
        }
      },
    }),
    // Who downloaded the loan's package (Document Generation → Download all
    // documents (ZIP)), and when, newest first. The server records each
    // download; the Documents tab invalidates this after one
    // (`invalidateAfterPackageDownload` in redux/services/mismo).
    getPackageDownloads: builder.query({
      query: ({ applicationId }) => ({
        url: `b2b/loan/mismo/${applicationId}/export/documents/history`,
        method: 'GET',
        headers: getHeaders(),
      }),
      transformResponse: (response) => response?.data ?? [],
      providesTags: ['packageDownloads'],
    }),
  }),
});

// api for view document is present in hooks > pdfViewer.js

export const {
  useGetDocumentListQuery,
  useUploadPredefinedDocsMutation,
  useUpdatePredefinedDocsMutation,
  useGetUserPropertyQuery,
  useDeleteDocumentMutation,
  useVerifyDocumentMutation,
  useRejectDocumentMutation,
  useUploadAndClassifyMutation,
  useLazyGetDocumentByIdQuery,
  useGetDocumentSummariesQuery,
  useGetDocumentAccuracyMetricsQuery,
  useGetPreApprovalEligibilityQuery,
  useGetPreApprovalLetterStatusQuery,
  useGetPreApprovalDraftQuery,
  usePreviewPreApprovalLetterMutation,
  useGeneratePreApprovalLetterMutation,
  useGetPackageDownloadsQuery,
} = adminDocumentAPI;
